import { useCallback, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { translateFarmsError } from '../lib/translateError';
import type { FarmNearby } from '../types';

interface Filters {
  lat:             number;
  lng:             number;
  radius_km?:      number;
  material?:       string;
  state_uf?:       string;
  specialization?: string;
}

export function useFarms() {
  const [farms, setFarms]     = useState<FarmNearby[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const search = useCallback(async (filters: Filters) => {
    setLoading(true);
    setError('');
    try {
      const { data, error: rpcErr } = await supabase.rpc('farms_nearby' as never, {
        p_lat:            filters.lat,
        p_lng:            filters.lng,
        p_radius_km:      filters.radius_km ?? 200,
        p_material:       filters.material ?? null,
        p_state_uf:       filters.state_uf ?? null,
        p_specialization: filters.specialization ?? null,
      } as never);
      if (rpcErr) throw new Error(translateFarmsError(rpcErr.message));
      setFarms((data as unknown as FarmNearby[]) ?? []);
    } catch (e: any) {
      setError(e.message ?? translateFarmsError(''));
    } finally {
      setLoading(false);
    }
  }, []);

  return { farms, loading, error, search };
}

// Geocodifica via OpenStreetMap Nominatim (gratuito, sem API key)
async function geocodeCity(city: string, state_uf: string): Promise<{ lat: number; lng: number } | null> {
  try {
    const q = encodeURIComponent(`${city}, ${state_uf}, Brasil`);
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1&countrycodes=br`,
      { headers: { 'User-Agent': 'MYND-BPO/1.0 (bemyndstore@gmail.com)' } }
    );
    if (!res.ok) return null;
    const results = await res.json() as Array<{ lat: string; lon: string }>;
    if (!results.length || !results[0]) return null;
    return { lat: parseFloat(results[0].lat), lng: parseFloat(results[0].lon) };
  } catch {
    return null;
  }
}

export function useCepLookup() {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  async function lookup(cep: string): Promise<{ lat: number | null; lng: number | null; city: string; state_uf: string; address_line: string } | null> {
    setLoading(true);
    setError('');
    try {
      const digits = cep.replace(/\D/g, '');
      if (digits.length !== 8) throw new Error('CEP inválido. Informe 8 dígitos.');

      const viaRes = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      if (!viaRes.ok) throw new Error('Serviço de CEP indisponível. Tente novamente.');
      const via = await viaRes.json() as { erro?: boolean; localidade?: string; uf?: string; logradouro?: string };
      if (via.erro) throw new Error('CEP não encontrado.');

      const city         = via.localidade ?? '';
      const state_uf     = via.uf ?? '';
      const address_line = via.logradouro ?? '';

      // Seed do br_cities_geo não tem acentos; normaliza para busca no cache
      const cityNorm = city.normalize('NFD').replace(/[̀-ͯ]/g, '');

      // 1. Tenta br_cities_geo (tabela local)
      const { data: geoRows } = await supabase
        .from('br_cities_geo')
        .select('lat, lng')
        .eq('state_uf', state_uf)
        .ilike('name', cityNorm)
        .limit(1);

      const cached = (geoRows as unknown as Array<{ lat: number; lng: number }> | null)?.[0];
      if (cached?.lat && cached?.lng) {
        return { lat: Number(cached.lat), lng: Number(cached.lng), city, state_uf, address_line };
      }

      // 2. Fallback: Nominatim (OpenStreetMap)
      const geo = await geocodeCity(city, state_uf);
      if (geo) {
        // ibge_code é PK NOT NULL — gera chave sintética; armazena sem acento para consistência
        const ibgeKey = `${state_uf.toUpperCase()}_${cityNorm.toLowerCase().replace(/[\s/]/g, '_')}`;
        await supabase.from('br_cities_geo').upsert(
          { ibge_code: ibgeKey, name: cityNorm, state_uf, lat: geo.lat, lng: geo.lng } as never,
          { onConflict: 'ibge_code', ignoreDuplicates: true },
        );
        return { lat: geo.lat, lng: geo.lng, city, state_uf, address_line };
      }

      // 3. Sem coordenadas — retorna dados parciais para não bloquear o usuário
      return { lat: null, lng: null, city, state_uf, address_line };
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }

  return { lookup, loading, error };
}
